import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["monthYear", "daysGrid"]
  static values = { month: Number, year: Number, minDate: String, maxDate: String }

  prevMonth() {
    if (this.monthValue === 1) {
      this.monthValue = 12
      this.yearValue--
    } else {
      this.monthValue--
    }
    this.refreshCalendar()
  }

  nextMonth() {
    if (this.monthValue === 12) {
      this.monthValue = 1
      this.yearValue++
    } else {
      this.monthValue++
    }
    this.refreshCalendar()
  }

  refreshCalendar() {
    // Update month/year display
    if (this.hasMonthYearTarget) {
      const monthNames = ["January", "February", "March", "April", "May", "June",
                         "July", "August", "September", "October", "November", "December"]
      this.monthYearTarget.textContent = `${monthNames[this.monthValue - 1]} ${this.yearValue}`
    }

    // Rebuild calendar days
    if (this.hasDaysGridTarget) {
      this.renderDays()
    }

    // Dispatch event for parent to handle
    this.dispatch("monthChanged", {
      detail: { month: this.monthValue, year: this.yearValue }
    })
  }

  renderDays() {
    const grid = this.daysGridTarget

    // Clear existing days (keep weekday headers - first 7 children)
    const weekdayHeaders = Array.from(grid.children).slice(0, 7)
    grid.innerHTML = ""
    weekdayHeaders.forEach(header => grid.appendChild(header))

    // Get days for current month
    const firstDay = new Date(this.yearValue, this.monthValue - 1, 1)
    const lastDay = new Date(this.yearValue, this.monthValue, 0)
    const daysInMonth = lastDay.getDate()

    // Get day of week for first day (0 = Sunday, adjust for Monday start)
    let startDay = firstDay.getDay()
    startDay = startDay === 0 ? 6 : startDay - 1 // Convert to Monday = 0

    // Previous month days
    const prevMonthLastDay = new Date(this.yearValue, this.monthValue - 1, 0).getDate()
    for (let i = startDay - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i
      const date = new Date(this.yearValue, this.monthValue - 2, day)
      this.addDayCell(grid, day, "inactive", date)
    }

    // Current month days
    const today = new Date()
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(this.yearValue, this.monthValue - 1, day)
      const isToday = date.toDateString() === today.toDateString()
      const type = isToday ? "today" : "active"
      this.addDayCell(grid, day, type, date)
    }

    // Next month days to fill remaining cells
    const totalCells = startDay + daysInMonth
    const remainingCells = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7)
    for (let day = 1; day <= remainingCells; day++) {
      const date = new Date(this.yearValue, this.monthValue, day)
      this.addDayCell(grid, day, "inactive", date)
    }
  }

  addDayCell(grid, day, type, date) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative"

    const cell = document.createElement("div")
    cell.dataset.action = "click->date-picker#selectDate"
    cell.dataset.date = this.formatDateISO(date)
    cell.textContent = day

    // Check if date is disabled
    const isDisabled = this.isDateDisabled(date)

    // Base classes
    let classes = "w-10 h-10 flex items-center justify-center text-sm relative rounded-full"

    if (isDisabled) {
      // Disabled state
      classes += " cursor-not-allowed pointer-events-none text-gray-600 font-normal"
      // Inactive disabled has no background, others have gray-100
      if (type !== "inactive") {
        classes += " bg-gray-100"
      }
    } else {
      classes += " cursor-pointer"
      // Type-based styling
      if (type === "inactive") {
        classes += " text-gray-600 font-normal"
      } else if (type === "today") {
        classes += " text-gray-900 font-medium bg-gray-50 hover:bg-gray-200"
      } else {
        classes += " text-gray-800 font-normal hover:bg-gray-50"
      }
    }

    cell.className = classes
    wrapper.appendChild(cell)
    grid.appendChild(wrapper)
  }

  isDateDisabled(date) {
    if (this.hasMinDateValue && this.minDateValue) {
      const minDate = new Date(this.minDateValue)
      if (date < minDate) return true
    }
    if (this.hasMaxDateValue && this.maxDateValue) {
      const maxDate = new Date(this.maxDateValue)
      if (date > maxDate) return true
    }
    return false
  }

  formatDateISO(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }
}
